import React, { useState, useMemo, useCallback, useEffect } from "react";
import { DESKTOP_INITIAL, MOBILE_INITIAL, getInitialVisible } from "./Detailconstants.js";
import { fetchContent } from "../../../../services/contentService";
import ContactModal from "./ContactModal.jsx";

function FormCard({ form, index }) {
  return (
    <article
      className="VF-form-card"
      data-aos="fade-up"
      data-aos-delay={Math.min(index * 70, 350)}
      aria-label={`${form.country} Forms`}
    >
      <div className="VF-form-card__header">
        <div>
          <h3 className="VF-form-card__country">{form.country ?? ""}</h3>
          <p className="VF-form-card__name">{form.formName ?? ""}</p>
        </div>
      </div>
      <p className="VF-form-card__description">{form.description}</p>
      {form.tags?.length > 0 && (
        <div className="VF-form-card__tags" aria-label="Form topics">
          {form.tags.map((tag) => (
            <span key={tag} className="VF-form-tag">{tag}</span>
          ))}
        </div>
      )}
      <a
        href={form.formUrl ?? "#"}
        target="_blank"
        rel="noopener noreferrer"
        className="VF-form-card__cta"
        aria-label={`Open ${form.formName ?? "form"} — opens in a new tab`}
      >
        <span style={{color:"white"}}>Visit {form.formName ?? "Form"}</span>
      </a>
    </article>
  );
}

export default function FormsSection() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [visibleCount, setVisibleCount] = useState(getInitialVisible);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetchContent("visaDetail").then(section => {
      const list = section?.content?.visaForms;
      if (Array.isArray(list) && list.length) {
        const mapped = list.map((f, idx) => ({
          id: f.id || `form-${idx}`,
          country: f.country || "",
          formName: f.form_name || f.formName || "",
          formUrl: f.form_url || f.formUrl || "",
          description: f.description || "",
          tags: f.tags || [],
        }));
        setForms(mapped);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => {
    const all = forms.map((f) => f.country).filter(Boolean);
    return [...new Set(all)];
  }, [forms]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return forms.filter((form) => {
      const matchCountry = !selectedCountry || form.country === selectedCountry;
      if (!matchCountry) return false;
      if (!q) return true;
      return (
        (form.country?.toLowerCase().includes(q) ?? false) ||
        (form.formName?.toLowerCase().includes(q) ?? false) ||
        (form.description?.toLowerCase().includes(q) ?? false) ||
        (form.tags?.some((t) => t?.toLowerCase().includes(q)) ?? false)
      );
    });
  }, [search, selectedCountry, forms]);

  const isOutOfScope = search.trim().length > 0 && filtered.length === 0;

  const handleClear = useCallback(() => {
    setSearch("");
    setSelectedCountry("");
    setVisibleCount(getInitialVisible());
  }, []);

  const hasFilters = search || selectedCountry;
  const visibleCards = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visibleCount;
  const canCollapse = visibleCount > getInitialVisible() && !hasMore;

  const handleViewMore = () => setVisibleCount((p) => p + (window.innerWidth <= 767 ? MOBILE_INITIAL : DESKTOP_INITIAL));
  const handleCollapse = () => setVisibleCount(getInitialVisible());

  return (
    <section className="VF-section VF-section--alt" id="visa-forms" aria-label="Visa Form Section">
      <div className="VF-container">
        <header className="VF-section-header" data-aos="fade-up">
          <h2 className="VF-heading-lg">Visa Form</h2>
          <span className="VF-divider" aria-hidden="true" />
          <p>Connect with country-specific visa communities. Search the forms directory below and access active discussion boards to get advice, share experiences and find answers.</p>
        </header>

        <div className="VF-filter-bar" data-aos="fade-up" data-aos-delay="80" role="search" aria-label="Filter visa forms">
          <div className="VF-filter-group VF-filter-group--search">
            {/* <label className="VF-filter-label" htmlFor="VF-forms-search">Search</label> */}
            <input
              id="VF-forms-search"
              type="text"
              className="VF-filter-input"
              placeholder="Search by country, form name or visa type..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(getInitialVisible()); }}
              aria-label="Search visa forms"
            />
          </div>
          <div className="VF-filter-group">
            {/* <label className="VF-filter-label" htmlFor="VF-forms-country">Country</label> */}
            <select id="VF-forms-country" className="VF-filter-select" value={selectedCountry} onChange={(e) => { setSelectedCountry(e.target.value); setVisibleCount(getInitialVisible()); }}>
              <option value="">Country</option>
              {countries.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {hasFilters && (
            <button className="VF-filter-reset" onClick={handleClear}>Reset Filters</button>
          )}
        </div>

        <div className="VF-results-info VF-results-info--mb" aria-live="polite" aria-atomic="true">
          <p className="VF-results-count">
            Showing <strong>{Math.min(visibleCount, filtered.length)}</strong> of <strong>{filtered.length}</strong> country forms
          </p>
        </div>

        {loading ? (
          <div className="VF-empty-state" role="status"><p className="VF-empty-state__text">Loading visa forms...</p></div>
        ) : filtered.length > 0 ? (
          <>
            <div className="VF-forms-grid" role="list">
              {visibleCards.map((form, i) => (
                <div key={form.id} role="listitem">
                  <FormCard form={form} index={i} />
                </div>
              ))}
            </div>
            {(hasMore || canCollapse) && (
              <div className="VF-view-more-wrapper">
                {hasMore && (
                  <button className="VF-btn-view-more" onClick={handleViewMore}>
                    Show More Forms
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    {/* <span className="VF-show-more-count">({filtered.length - visibleCount} more)</span> */}
                  </button>
                )}
                {canCollapse && (
                  <button className="VF-btn-view-more VF-btn-view-more--collapse" onClick={handleCollapse}>
                    Show Less
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="VF-empty-state" data-aos="fade-up" role="status">
            <h3 className="VF-empty-state__title">No forms found</h3>
            <p className="VF-empty-state__text">
              {isOutOfScope
                ? "No form matched your search. Would you like to request information about a specific country or visa type?"
                : "No form matched your search. Try a different country name or visa type."}
            </p>
            <div className="VF-empty-state__actions">
              <button className="VF-btn-secondary" onClick={handleClear}>Clear Search</button>
              {isOutOfScope && (
                <button className="VF-btn-primary" onClick={() => setModalOpen(true)}>Request Form Info</button>
              )}
            </div>
          </div>
        )}

        <div className="VF-forms-disclaimer" data-aos="fade-up" role="note">
          <p>
            <strong>Please note:</strong> The forms listed are independent community platforms and are not affiliated with any government body or official immigration authority. Always cross-reference information with the official embassy or government immigration portal of the respective country.
          </p>
        </div>
      </div>

      <ContactModal isOpen={modalOpen} onClose={() => setModalOpen(false)} query={search} sectionName="Visa Form" />
    </section>
  );
}
